import {DeliveryInGroup} from "../../../types/schedule/delivery-in-group";
import {Schedule} from "../../../types/schedule/schedule";
import {ScheduleDto} from "../../../types/schedule/schedule-dto";
import {convertUserDtoToUser} from "../user/user-converter";
import {convertDeliveryGroupDtoToDeliveryGroup} from "./delivery-group-converter";
import {convertDeliveryInGroupDtoToDeliveryInGroup} from "./delivery-in-group-converter";
import {convertDeliverySegmentDtoToDeliverySegment} from "./delivery-segment-converter";
import {ScheduleStatus} from "../../../types/schedule/schedule-status";

export const convertScheduleDtoToSchedule = (
    scheduleDto: ScheduleDto
): Schedule => {
    const deliveriesInSchedule = getDeliveriesInGroup(scheduleDto)
    const hasMultipleClients = getHasMultipleClients(deliveriesInSchedule)
    const schedule: Schedule = {
        id: scheduleDto.id,
        worker: scheduleDto.worker && convertUserDtoToUser(scheduleDto.worker),
        alias: scheduleDto.alias,
        dateToWork: scheduleDto.dateToWork,
        deliveryGroups: scheduleDto.deliveryGroups.map((deliveryGroupDto, index) => {
            return convertDeliveryGroupDtoToDeliveryGroup(
                deliveryGroupDto,
                scheduleDto,
                isNotScheduleWait(scheduleDto) && scheduleDto.currentDeliveryGroupIndex === index,
                isScheduleWait(scheduleDto) || scheduleDto.currentDeliveryGroupIndex < index,
            );
        }),
        startTimeLimited: scheduleDto.startTimeLimited,
        currentDeliveryGroupIndex: scheduleDto.currentDeliveryGroupIndex,
        status: scheduleDto.status,
        startedAt: scheduleDto.startedAt,
        updatedAt: scheduleDto.updatedAt,
        completedAt: scheduleDto.completedAt,

        clientNames: getClientNames(scheduleDto),
        clientIds: getClientIds(scheduleDto),
        deliveriesInGroup: deliveriesInSchedule,
        currentDeliveryGroup: convertDeliveryGroupDtoToDeliveryGroup(
            scheduleDto.deliveryGroups[scheduleDto.currentDeliveryGroupIndex] ||
            scheduleDto.deliveryGroups.at(-1),
            scheduleDto,
            isScheduleWork(scheduleDto),
            isScheduleWait(scheduleDto),
        ),
        deliverySegments: scheduleDto.deliverySegments.map((deliverySegmentDto) =>
            convertDeliverySegmentDtoToDeliverySegment(deliverySegmentDto)
        ),
        hasMultipleClients: hasMultipleClients,
        agencyId: deliveriesInSchedule[0].agency.id,
    };

    if (hasMultipleClients) {
        return assignClientSequence(schedule)
    } else {
        return schedule
    }
};

const getClientNames = (scheduleDto: ScheduleDto): string[] => {
    const deliveryDtos = scheduleDto.deliveryGroups.flatMap(
        (deliveryGroupDto) => {
            return deliveryGroupDto.deliveriesInGroup;
        }
    );
    return [
        ...new Set(
            deliveryDtos.map((deliveryDto) => {
                return deliveryDto.client.name;
            })
        ),
    ];
};

const isScheduleWait = (scheduleDto: ScheduleDto): boolean => scheduleDto.status === ScheduleStatus.Wait
const isNotScheduleWait = (scheduleDto: ScheduleDto): boolean => scheduleDto.status !== ScheduleStatus.Wait
const isScheduleWork = (scheduleDto: ScheduleDto): boolean => scheduleDto.status === ScheduleStatus.Work

const getClientIds = (scheduleDto: ScheduleDto): string[] => {
    const deliveryDtos = scheduleDto.deliveryGroups.flatMap(
        (deliveryGroupDto) => {
            return deliveryGroupDto.deliveriesInGroup;
        }
    );
    return [
        ...new Set(
            deliveryDtos.map((deliveryDto) => {
                return deliveryDto.client.id;
            })
        ),
    ];
};

const getDeliveriesInGroup = (scheduleDto: ScheduleDto): DeliveryInGroup[] => {
    const deliveries = scheduleDto.deliveryGroups.flatMap((deliveryGroupDto) => {
        return deliveryGroupDto.deliveriesInGroup.map((deliveryInGroupDto) =>
            convertDeliveryInGroupDtoToDeliveryInGroup(
                deliveryInGroupDto,
                scheduleDto
            )
        );
    });
    return [...new Set(deliveries)];
};

const getHasMultipleClients = (deliveriesInSchedule: DeliveryInGroup[]): boolean => {
    const clientNames = new Set(deliveriesInSchedule.map(delivery => delivery.client.name));
    return clientNames.size > 1;
}

const assignClientSequence = (schedule: Schedule): Schedule => {
  const clientCountMap: { [clientName: string]: number } = {};

  schedule.deliveryGroups.forEach(group => {
    if (group.deliveriesInGroup.length > 0) {
      const clientName = group.deliveriesInGroup[0].client.name;

      if (!clientCountMap[clientName]) {
        clientCountMap[clientName] = 0;
      }
      clientCountMap[clientName] += 1;

      group.clientSequence = `${clientName[0]} ${clientCountMap[clientName]}`;
    }
  });

  return schedule;
};
